import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { StreetViewImage } from "./StreetViewImage";

const money = (n) => `$${Math.round(n).toLocaleString("en-US")}`;
const fmtDate = (d) => {
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" }); }
  catch { return d; }
};

// Live "Recent market activity" strip pulled from RentCast MLS sale-listing data for
// the neighborhood ZIP (homes recently listed / taken off the market, at list price —
// NOT verified closing prices). Renders nothing unless the backend returns real data.
export const RecentActivityStrip = ({ zip, areaLabel }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/market/recent-activity", { params: { zip, limit: 6 } });
        if (active) setItems(data.available ? data.listings : []);
      } catch { if (active) setItems([]); }
    })();
    return () => { active = false; };
  }, [zip]);

  if (!items || items.length === 0) return null;

  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-14" data-testid="recent-activity-strip">
      <span className="eyebrow">Live Market Proof</span>
      <h2 className="mt-2 font-serif text-3xl text-brand-ink">Recent market activity near {areaLabel || zip}</h2>
      <p className="mt-2 text-sm text-brand-stone max-w-3xl">Homes recently listed and taken off the market in ZIP {zip}, from MLS data via RentCast, most recent first — a live read on local pricing and demand.</p>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((s, i) => (
          <div key={i} className="rounded-2xl bg-white border border-brand-hairline overflow-hidden shadow-[0_4px_20px_rgba(11,30,140,0.04)]" data-testid="recent-activity-card">
            <div className="relative h-40 bg-brand-sand">
              <StreetViewImage
                lat={s.lat} lng={s.lng} w={600} h={320}
                alt={`Home at ${s.address}`}
                className="h-40 w-full object-cover"
                fallback={<div className="h-40 w-full grid place-items-center text-brand-stone/50 text-xs uppercase tracking-wider">No image</div>}
              />
              <span className="absolute top-3 left-3 rounded-full bg-brand-emerald/95 px-3 py-1 text-xs font-semibold text-brand-cream">Off-market {fmtDate(s.removed_date)}</span>
            </div>
            <div className="p-5">
              <p className="font-mono text-2xl font-bold text-brand-emerald">{money(s.list_price)}</p>
              <p className="text-[0.65rem] uppercase tracking-wider text-brand-stone/70">Last list price</p>
              <p className="mt-2 text-sm text-brand-ink font-medium leading-snug">{s.address}</p>
              <p className="mt-2 text-xs text-brand-stone">
                {[s.beds ? `${s.beds} bd` : null, s.baths ? `${s.baths} ba` : null,
                  s.living_area_sqft ? `${s.living_area_sqft.toLocaleString()} sqft` : null,
                  s.price_per_sqft ? `${money(s.price_per_sqft)}/sqft` : null]
                  .filter(Boolean).join(" · ")}
              </p>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-brand-stone/70">Figures are MLS list prices (RentCast does not provide verified closing prices), and a listing leaving the market may reflect a sale, withdrawal, or expiration. This is market context, not a valuation of your home — request an address-specific estimate above.</p>
    </section>
  );
};

export default RecentActivityStrip;
